import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Map, FileText, Mail, MessageSquare, Trash2, Edit, Plus, LogOut, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [destinations, setDestinations] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editingDest, setEditingDest] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const headers = getHeaders();
      const [dRes, bRes, qRes, sRes] = await Promise.all([
        axios.get(`${API}/destinations`),
        axios.get(`${API}/blog`),
        axios.get(`${API}/quotes`, { headers }),
        axios.get(`${API}/newsletter`, { headers }),
      ]);
      setDestinations(dRes.data);
      setBlogPosts(bRes.data);
      setQuotes(qRes.data);
      setSubscribers(sRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expiree");
        navigate("/admin");
      }
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return; }
    fetchAll();
  }, [navigate, fetchAll]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const deleteDest = async (id) => {
    if (!window.confirm("Supprimer cette destination ?")) return;
    try {
      await axios.delete(`${API}/destinations/${id}`, { headers: getHeaders() });
      toast.success("Destination supprimee");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await axios.delete(`${API}/blog/${id}`, { headers: getHeaders() });
      toast.success("Article supprime");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const reseed = async () => {
    if (!window.confirm("Reinitialiser toutes les donnees ? Cette action est irreversible.")) return;
    try {
      await axios.post(`${API}/seed`, null, { headers: getHeaders() });
      toast.success("Donnees reinitialisees");
      fetchAll();
    } catch { toast.error("Erreur lors de la reinitialisation"); }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      {/* Admin header */}
      <header className="bg-[#2D5016] text-[#FDF8F0] px-6 py-4 flex items-center justify-between" data-testid="admin-header">
        <div className="flex items-center gap-4">
          <Logo size="small" />
          <span className="font-body text-sm bg-[#C8A951]/20 px-3 py-1 rounded-full">Administration</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="font-body text-sm text-[#FDF8F0]/70 hover:text-[#FDF8F0]">Voir le site</Link>
          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-[#FDF8F0]/70 hover:text-[#FDF8F0] hover:bg-white/10" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Deconnexion
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Destinations", value: destinations.length, icon: Map, color: "#2D5016" },
            { label: "Articles", value: blogPosts.length, icon: FileText, color: "#A0522D" },
            { label: "Demandes devis", value: quotes.length, icon: MessageSquare, color: "#C8A951" },
            { label: "Newsletter", value: subscribers.length, icon: Mail, color: "#2D5016" },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-[#DBCFB0]/30" data-testid={`admin-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <stat.icon className="w-5 h-5 mb-2" style={{ color: stat.color }} />
              <p className="font-heading text-2xl font-bold text-[#2C2C2C]">{stat.value}</p>
              <p className="font-body text-xs text-[#6B6B6B]">{stat.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="destinations" className="space-y-6">
          <TabsList className="bg-white border border-[#DBCFB0]/30 p-1 rounded-lg">
            <TabsTrigger value="destinations" className="font-body text-sm data-[state=active]:bg-[#2D5016] data-[state=active]:text-white">Destinations</TabsTrigger>
            <TabsTrigger value="blog" className="font-body text-sm data-[state=active]:bg-[#2D5016] data-[state=active]:text-white">Blog</TabsTrigger>
            <TabsTrigger value="quotes" className="font-body text-sm data-[state=active]:bg-[#2D5016] data-[state=active]:text-white">Devis</TabsTrigger>
            <TabsTrigger value="newsletter" className="font-body text-sm data-[state=active]:bg-[#2D5016] data-[state=active]:text-white">Newsletter</TabsTrigger>
          </TabsList>

          {/* DESTINATIONS TAB */}
          <TabsContent value="destinations" data-testid="admin-destinations-tab">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-2xl font-semibold text-[#2C2C2C]">Destinations ({destinations.length})</h2>
              <Button onClick={reseed} variant="outline" size="sm" className="font-body text-xs border-[#DBCFB0]">
                <RefreshCw className="w-3 h-3 mr-1" /> Reinitialiser
              </Button>
            </div>
            <div className="bg-white rounded-xl border border-[#DBCFB0]/30 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#EFEBE5]">
                  <tr>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Destination</th>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider hidden md:table-cell">Continent</th>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider hidden md:table-cell">Duree</th>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Prix</th>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Vedette</th>
                    <th className="px-4 py-3 text-right font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DBCFB0]/30">
                  {destinations.map(d => (
                    <tr key={d.id} className="hover:bg-[#FDF8F0]/50">
                      <td className="px-4 py-3 font-body text-sm font-medium text-[#2C2C2C]">{d.name}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#6B6B6B] capitalize hidden md:table-cell">{d.continent}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#6B6B6B] hidden md:table-cell">{d.duration}j</td>
                      <td className="px-4 py-3 font-body text-sm font-medium text-[#2D5016]">{d.price} EUR</td>
                      <td className="px-4 py-3 font-body text-sm">{d.is_featured ? "Oui" : "Non"}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => deleteDest(d.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-dest-${d.slug}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* BLOG TAB */}
          <TabsContent value="blog" data-testid="admin-blog-tab">
            <h2 className="font-heading text-2xl font-semibold text-[#2C2C2C] mb-4">Articles ({blogPosts.length})</h2>
            <div className="bg-white rounded-xl border border-[#DBCFB0]/30 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#EFEBE5]">
                  <tr>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Titre</th>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider hidden md:table-cell">Date</th>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider hidden md:table-cell">Lecture</th>
                    <th className="px-4 py-3 text-right font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DBCFB0]/30">
                  {blogPosts.map(p => (
                    <tr key={p.id} className="hover:bg-[#FDF8F0]/50">
                      <td className="px-4 py-3 font-body text-sm font-medium text-[#2C2C2C]">{p.title}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#6B6B6B] hidden md:table-cell">{p.published_date}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#6B6B6B] hidden md:table-cell">{p.read_time} min</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => deleteBlog(p.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-blog-${p.slug}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* QUOTES TAB */}
          <TabsContent value="quotes" data-testid="admin-quotes-tab">
            <h2 className="font-heading text-2xl font-semibold text-[#2C2C2C] mb-4">Demandes de devis ({quotes.length})</h2>
            <div className="space-y-4">
              {quotes.length === 0 ? (
                <p className="font-body text-[#6B6B6B] text-center py-8">Aucune demande de devis pour le moment.</p>
              ) : quotes.map(q => (
                <div key={q.id} className="bg-white rounded-xl p-5 border border-[#DBCFB0]/30 shadow-sm" data-testid={`quote-item-${q.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-body text-sm font-bold text-[#2C2C2C]">{q.last_name} {q.first_name}</p>
                      <p className="font-body text-xs text-[#6B6B6B]">{q.email} | {q.phone}</p>
                    </div>
                    <span className="bg-[#2D5016]/10 text-[#2D5016] font-body text-xs font-medium px-3 py-1 rounded-full">{q.destination}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs font-body text-[#6B6B6B] mb-2">
                    <span>Voyageurs: {q.travelers}</span>
                    <span>Dates: {q.dates}</span>
                    <span>Budget: {q.budget}</span>
                  </div>
                  {q.message && <p className="font-body text-sm text-[#2C2C2C]/70 italic bg-[#FDF8F0] p-3 rounded-lg">{q.message}</p>}
                  <p className="font-body text-xs text-[#6B6B6B] mt-2">Recu le {new Date(q.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* NEWSLETTER TAB */}
          <TabsContent value="newsletter" data-testid="admin-newsletter-tab">
            <h2 className="font-heading text-2xl font-semibold text-[#2C2C2C] mb-4">Inscrits newsletter ({subscribers.length})</h2>
            <div className="bg-white rounded-xl border border-[#DBCFB0]/30 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#EFEBE5]">
                  <tr>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left font-body text-xs text-[#6B6B6B] uppercase tracking-wider">Date d'inscription</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DBCFB0]/30">
                  {subscribers.map(s => (
                    <tr key={s.id} className="hover:bg-[#FDF8F0]/50">
                      <td className="px-4 py-3 font-body text-sm text-[#2C2C2C]">{s.email}</td>
                      <td className="px-4 py-3 font-body text-sm text-[#6B6B6B]">{new Date(s.subscribed_at).toLocaleDateString("fr-FR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
